/**
 * Announcement Popup - Shopify Theme Component
 * 
 * A lightweight popup modal for displaying announcements that respects user dismissal.
 * 
 * FEATURES:
 * - Shows popup with configurable delay
 * - Remembers dismissal via localStorage
 * - Keyboard accessible (ESC key to close)
 * - Click overlay or close button to dismiss
 * - Prevents body scrolling when open
 * - Mobile responsive
 * 
 * USAGE:
 * Include in theme.liquid: {% render 'announcement-popup' %}
 * 
 * SETTINGS (configured in settings_schema.json):
 * - announcement_popup_enabled: Enable/disable popup
 * - announcement_popup_delay: Delay before showing (seconds)
 * - announcement_popup_image: Optional popup image
 * - announcement_popup_title: Popup title text
 * - announcement_popup_content: Rich text content
 * - announcement_popup_show_social: Show social media icons
 * 
 * HTML STRUCTURE:
 * <announcement-popup data-popup-enabled="true" data-section-id="announcement-popup-global">
 *   <div class="announcement-popup-modal">
 *     <div class="announcement-popup-modal__overlay"></div>
 *     <div class="announcement-popup-modal__content">
 *       <button data-close-popup>Close</button>
 *       <!-- Content here -->
 *     </div>
 *   </div>
 * </announcement-popup>
 * 
 * CSS CLASSES:
 * - .announcement-popup-modal: Main modal container
 * - .announcement-popup-modal.active: When popup is visible
 * - .announcement-popup-modal.closing: During close animation
 * - .announcement-popup-modal__overlay: Background overlay
 * - .announcement-popup-modal__content: Content container
 * 
 * LOCALSTORAGE:
 * Stores dismissal state as: announcement-popup-closed-{sectionId} = "true"
 * 
 * BROWSER SUPPORT:
 * - Modern browsers with Custom Elements support
 * - Gracefully degrades if localStorage unavailable
 * 
 */

/**
 * Simple Announcement Popup
 */
class AnnouncementPopup extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.hasShown = false;
    this.isVisible = false;
  }

  connectedCallback() {
    this.modal = this.querySelector('.announcement-popup-modal');
    if (!this.modal) return;
    
    this.setupEvents();
    this.initPopup();
  }
  
  setupEvents() {
    // Close button
    const closeBtn = this.querySelector('[id^="ModalClose-"], [data-close-popup]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }
    
    // Overlay click
    const overlay = this.querySelector('.announcement-popup-modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.hide());
    }
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    });
  }
  
  initPopup() {
    if (this.shouldShow()) {
      const section = this.closest('section');
      const delay = section ? parseInt(section.dataset.popupDelay) || 3000 : 3000;
      setTimeout(() => this.show(), Math.max(0, Math.min(delay, 30000)));
    }
  }
  
  shouldShow() {
    try {
      const sectionId = this.dataset.sectionId || 'announcement-popup-global';
      const closedKey = `announcement-popup-closed-${sectionId}`;
      
      return !this.hasShown && 
             this.dataset.popupEnabled === 'true' && 
             localStorage.getItem(closedKey) !== 'true';
    } catch {
      return !this.hasShown && this.dataset.popupEnabled === 'true';
    }
  }
  
  show() {
    if (this.isVisible) return;
    
    this.isVisible = true;
    this.hasShown = true;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.modal.classList.add('active');
  }
  
  hide() {
    if (!this.isVisible) return;
    
    this.isVisible = false;
    this.modal.classList.add('closing');
    document.body.classList.remove('overflow-hidden');
    
    // Save to localStorage
    try {
      const sectionId = this.dataset.sectionId || 'announcement-popup-global';
      const closedKey = `announcement-popup-closed-${sectionId}`;
      localStorage.setItem(closedKey, 'true');
    } catch {}
    
    // Complete hide after animation
    setTimeout(() => {
      this.modal.classList.remove('active', 'closing');
      this.modal.setAttribute('aria-hidden', 'true');
    }, 300);
  }
}

// Register custom element
if (!customElements.get('announcement-popup')) {
  customElements.define('announcement-popup', AnnouncementPopup);
} 